/**
 * Every configurable value in one place. `DEFAULTS` is what a fresh workspace
 * looks like; the settings screens edit a copy of it and persist through the
 * storage layer.
 */
export const DEFAULTS = {
  /* timings that drive the trial meter, renewal warnings and ageing buckets */
  trialDays: 14,
  renewWindow: 30,
  staleDays: 30,
  snoozeDays: 7,
  ageMid: 7,
  ageLate: 30,

  /* value lists */
  types: ['Paying', 'Trial', 'Free', 'Internal', 'Test'],
  excluded: ['Internal', 'Test'],
  plans: ['FREE', 'STARTER', 'GROWTH', 'SUITE'],
  statuses: ['Active', 'Trial', 'Inactive', 'On Hold', 'Expired', 'Churned'],
  liveStatuses: ['Active', 'Trial', 'On Hold'],
  payments: ['NO', 'YES', 'PENDING', 'PARTIAL', 'OVERDUE', 'REFUNDED', 'FAILED', 'N/A'],
  paidValues: ['YES'],
  cycles: ['-', 'Monthly', 'Quarterly', 'Half-Yearly', 'Annual', 'Lifetime'],
  sources: ['Direct', 'Website', 'Referral', 'Social Media', 'Campaign', 'Partner', 'Reseller', 'Walk-in', 'Other'],
  /* The sales team who carry the pipeline. Editable in Settings → Sales →
     Owners; this is only what a fresh org starts with. */
  owners: [
    'Mohan', 'Mohammed Sameer', 'Athulya', 'Sanjay', 'Swathi',
    'Aruna', 'Dharaneshwaran', 'Madhumitha', 'Sonalika', 'Kavya S',
    'Shajan Williams', 'Ashiq Afridi', 'Muralidharan K', 'Keerthana',
    'Geo Dabin Singh'
  ],
  hidden: ['sno', 'progress', 'lastContacted', 'aiRisk', 'lastPaid'],

  /* which conditions raise a flag */
  flagNoOwner: true,
  flagNoContact: true,
  flagStale: true,
  flagNoTrialDate: true,

  /* AI */
  aiOn: true,
  tone: 'Friendly and direct',
  senderName: 'Agalya',
  companyName: 'XYRA AI',

  /* pricing */
  currency: 'AED',
  /*
   * Prices carries one plan that plans does not.
   *
   * XYRA sells FREE, STARTER, GROWTH and SUITE - those four are the list
   * above, and they are what a plan picker offers. PROFESSIONAL is retired
   * and nothing new goes onto it, but seventeen accounts are still there
   * and one of them pays; leaving it unpriced valued all of them at zero
   * and dropped them out of billing.
   *
   * So it is priced and not offered. It is also why nothing treats the
   * order of this list as a ladder: a retired plan sits in the middle of
   * it, and a move onto one is not a promotion.
   */
  prices: { FREE: 0, STARTER: 49, PROFESSIONAL: 49, GROWTH: 99, SUITE: 199 },
  annualDiscount: 20,
  modules: {
    'XYRA Inventory': 0,
    'XYRA Expenses': 0,
    'XYRA CFO': 0,
    'XYRA Legal Docs': 0,
    'XYRA Payroll (WPS)': 0,
    'XYRA E-sign': 0
  },
  bundlePlans: ['SUITE'],
  upsellDays: 60,
  /* Set from the server's `clientsApp`. When the clients app is the source of
     truth it owns the amount, so a blank one stays blank instead of falling
     back to the list price above. Offline and plain-MySQL modes leave this
     false and keep the fallback, which is all the sample data has. */
  clientsApp: false,

  /* pipeline — the one stage-driven board (build decision 2026-10). These are
     the source of truth, merged over the org's own settings.stages/subStatuses
     so an admin can change them: nothing about the stages is hardcoded in a
     component, which reads S.stages / S.subStatuses. */
  stages: ['New', 'Demo', 'Proposal', 'Won', 'Lost'],
  probs: { New: 10, Demo: 45, Proposal: 65, Won: 100, Lost: 0 },
  /* The status within a stage. New and Won carry none; Lost gathers the ways a
     lead ends without a win. */
  subStatuses: {
    New: [],
    Demo: ['Booked', 'Done'],
    Proposal: ['Sent', 'Negotiating'],
    Won: [],
    Lost: ['Lost', 'Not interested', 'No response']
  },
  lStatus: ['New', 'Contacted', 'Qualified', 'Nurturing', 'Unqualified', 'Customer'],
  industries: ['Trading', 'Contracting', 'Retail', 'Logistics', 'Healthcare', 'Professional services', 'Hospitality', 'Real estate'],
  actTypes: ['Call', 'Email', 'Meeting', 'Demo', 'WhatsApp', 'Note'],
  stallDays: 7,
  target: 50000,

  /* display */
  startView: 'dashboard',
  startMode: 'list',
  /* Board-or-table choice per CRM page. Defaults match how each has always opened. */
  crmMode: { pipeline: 'list', deals: 'list', companies: 'grid' },
  density: 'comfortable',
  /* Who a demo follow-up goes to when nobody has said. B2B partners and B2C
     customers are worked by different people. */
  b2bOwner: '',
  b2cOwner: '',
  dateFmt: 'long',
  dashRows: 8,
  queueMax: 60,
  aiTokens: 1400,
  aiRows: 200,
  theme: 'light',
  userName: 'Client Master',

  /* profile */
  myTitle: '',
  myEmail: '',
  myPhone: '',
  tz: 'Asia/Dubai',
  hours: '09:00-18:00',
  signature: '',

  /* organisation */
  legalName: '',
  licence: '',
  trn: '',
  vat: 5,
  city: 'Dubai',
  country: 'United Arab Emirates',
  supportEmail: '',
  website: '',
  fyStart: 'January',
  address: '',
  workspace: 'Client Master',
  depts: ['Implementation', 'Compliance', 'Growth', 'Partner', 'Product', 'Marketing'],

  /* team, permissions, audit */
  brand: null,
  people: null,
  perms: {},
  notif: {},
  audit: [],
  auditOn: true,

  /* privacy and lock */
  maskData: false,
  pinHash: '',
  autoLock: 0,
  quietOn: false,
  quietFrom: 19,
  quietTo: 8,
  weeklyOn: false,

  /* alerting */
  desktopAlerts: false,
  soundAlerts: true,
  autoBrief: false,
  checkMins: 5,
  minSeverity: 1,
  alertTypes: { upsell: false },
  dueSoonDays: 5,
  escalateHours: 24,

  /* scheduled jobs */
  digestOn: false,
  digestHour: 9,
  digestTo: '',
  digestSlack: false,
  autoRisk: false,
  riskHour: 2,
  slackWebhook: '',
  rrIndex: 0,
  lastRisk: '',

  /* automation */
  rules: [
    { id: 'r1', name: 'Convert ending trials', on: 'trial-soon', assign: '', status: '', channel: 'email', cooldown: 7, note: '', draft: true, notify: true, enabled: true },
    { id: 'r2', name: 'Chase overdue payments', on: 'unpaid', assign: '', status: '', channel: 'email', cooldown: 7, note: '', draft: true, notify: true, enabled: false },
    { id: 'r3', name: 'Auto-assign unowned accounts', on: 'unowned', assign: '__rr', status: '', channel: 'email', cooldown: 0, note: 'Auto-assigned by rule', draft: false, notify: true, enabled: true },
    { id: 'r0', name: 'Monthly invoice reminder', on: 'due-soon', assign: '', status: '', channel: 'email', cooldown: 25, note: '', draft: true, notify: true, enabled: true },
    { id: 'r5', name: 'Overdue payment chase', on: 'overdue', assign: '', status: '', channel: 'whatsapp', cooldown: 5, note: '', draft: true, notify: true, enabled: false },
    { id: 'r4', name: 'Win back expired', on: 'expired', assign: '', status: '', channel: 'whatsapp', cooldown: 14, note: '', draft: true, notify: true, enabled: false }
  ]
};

/** Editable value lists surfaced on Settings -> Lists. */
export const LISTS = [
  { k: 'types', n: 'Account types', d: 'How each account is classified. Excluded types stay out of every metric.' },
  { k: 'plans', n: 'Plans', d: 'Your subscription tiers, exactly as you sell them.' },
  { k: 'statuses', n: 'Statuses', d: 'Lifecycle states an account can be in.' },
  { k: 'payments', n: 'Payment statuses', d: 'Values the payment column can take.' },
  { k: 'cycles', n: 'Billing cycles', d: 'How often an account is billed.' },
  { k: 'sources', n: 'Registration sources', d: 'Where the account came from.' },
  { k: 'owners', n: 'Owners', d: 'People who can be assigned an account.' }
];

/**
 * The client record, described once. Drives the table columns, the edit sheet,
 * the column picker and the CSV export — add a field here and it appears in
 * all four.
 *
 *   k     field key on the record
 *   h     column heading
 *   t     text | opt (fixed list) | list (list plus "add new") | mods | area | derived
 *   g     group heading in the edit sheet
 *   src   which DEFAULTS list feeds an opt/list field
 *   wide  spans both columns in the sheet
 */
export const FIELDS = [
  { k: 'sno', h: 'S.No', t: 'text', g: 'Identity' },
  { k: 'name', h: 'Client', t: 'text', g: 'Identity', wide: 1 },
  { k: 'email', h: 'Email', t: 'text', g: 'Identity', wide: 1 },
  { k: 'phone', h: 'Phone', t: 'text', g: 'Identity' },
  { k: 'acctType', h: 'Type', t: 'opt', g: 'Identity', src: 'types' },
  { k: 'regOn', h: 'Registered', t: 'text', g: 'Lifecycle', hint: '05 May 2026 10:59' },
  { k: 'source', h: 'Source', t: 'list', g: 'Lifecycle', src: 'sources' },
  { k: 'promo', h: 'Promo', t: 'text', g: 'Lifecycle' },
  /*
   * Who brought them in, where a partner did — the name and the code the
   * partner hands to their customers, in two columns of their own.
   *
   * Two rather than one stacked cell because they are sorted on separately:
   * "everything Shabeeb sent" and "everyone who came in on THEONEAC-XXEP"
   * are different questions, and a heading answers each of them in a click.
   * The rest of the partner's details are on the client panel, where there
   * is room to name them.
   *
   * Blank on nearly every row, because nearly everybody came directly.
   */
  { k: 'partner', h: 'Partner', t: 'derived', g: 'Lifecycle' },
  { k: 'partnerPromo', h: 'Partner promo code', t: 'derived', g: 'Lifecycle' },
  { k: 'plan', h: 'Plan', t: 'opt', g: 'Lifecycle', src: 'plans' },
  { k: 'trialEnd', h: 'Trial', t: 'text', g: 'Lifecycle', hint: 'date, or "No trial"' },
  { k: 'progress', h: 'Lifecycle', t: 'derived', g: 'Lifecycle' },
  { k: 'expiry', h: 'Expiry', t: 'text', g: 'Lifecycle' },
  { k: 'cycle', h: 'Cycle', t: 'opt', g: 'Lifecycle', src: 'cycles' },
  { k: 'addons', h: 'Add-ons', t: 'mods', g: 'Billing', wide: 1 },
  { k: 'amount', h: 'Amount', t: 'text', g: 'Billing', hint: 'per billing period' },
  { k: 'nextDue', h: 'Next Due', t: 'text', g: 'Billing' },
  { k: 'lastPaid', h: 'Last Paid', t: 'text', g: 'Billing' },
  /*
   * What they paid, and on what terms, in one cell.
   *
   * The Payments page is asked one question — how much, monthly or yearly —
   * and it was answered across three columns that are easy to switch off and
   * easy to read past: Amount in one, Cycle four columns away, Last Paid
   * further still. This puts the three together in the order somebody says
   * them out loud.
   */
  { k: 'paidWhat', h: 'Paid', t: 'derived', g: 'Billing' },
  { k: 'pay', h: 'Payment', t: 'opt', g: 'Billing', src: 'payments' },
  { k: 'status', h: 'Status', t: 'opt', g: 'Billing', src: 'statuses' },
  { k: 'handler', h: 'Owner', t: 'list', g: 'Ownership', src: 'owners' },
  { k: 'contact', h: 'Contact', t: 'text', g: 'Ownership' },
  { k: 'lastActivity', h: 'Last Activity', t: 'text', g: 'Engagement' },
  { k: 'lastContacted', h: 'Last Contacted', t: 'text', g: 'Engagement' },
  { k: 'aiRisk', h: 'AI Risk', t: 'derived', g: 'Engagement' },
  { k: 'notes', h: 'Notes', t: 'area', g: 'Engagement', wide: 1 }
];

/** Column heading, with the trial column named after the configured length. */
export const headingOf = (field, S) =>
  field.k === 'trialEnd' ? `${S.trialDays}-Day Trial` : field.h;

/** Sidebar. A `g` entry starts a collapsible group. */
export const NAV = [
  { g: 'SALES' },
  { v: 'sales', n: 'Overview', i: 'grid', c: 'g-purple', path: '/sales' },
  { v: 'pipeline', n: 'Pipeline', i: 'zap', c: 'g-green', path: '/pipeline' },
  /* Retired from the sidebar: leads and deals are one table now, and the
     Pipeline board carries Won and Lost as stages — Deals was just the Won
     slice of the same rows. The route stays (hidden) so any old link still
     resolves; delete `hidden` to bring the menu item back. */
  { v: 'deals', n: 'Deals', i: 'card', c: 'g-orange', path: '/deals', hidden: true },
  { v: 'contacts', n: 'Contacts', i: 'users', c: 'g-blue', path: '/contacts' },
  // Hidden from the sidebar, but kept here so /companies keeps its page title
  // and its view key. Delete `hidden` to bring the link back.
  { v: 'companies', n: 'Companies', i: 'building', c: 'g-cyan', path: '/companies', hidden: true },
  { v: 'activities', n: 'Activities', i: 'cal', c: 'g-pink', path: '/activities' },
  { v: 'follow-ups', n: 'Follow-ups', i: 'flag', c: 'g-amber', path: '/follow-ups' },
  /* Reads the four lists this module already has - no data of its own, so
     it is open to anybody who can open the pipeline. */
  { v: 'sales-reports', n: 'Reports', i: 'chart', c: 'g-purple', path: '/sales-reports', perm: ['pipeline', 'view'] },
  { g: 'CLIENTS' },
  { v: 'dashboard', n: 'Dashboard', i: 'grid', c: 'g-purple', path: '/dashboard' },
  { v: 'clients', n: 'Clients', i: 'users', c: 'g-green', path: '/clients' },
  { v: 'trials', n: 'Trials', i: 'clock', c: 'g-amber', path: '/trials' },
  { v: 'payments', n: 'Payments', i: 'card', c: 'g-orange', path: '/payments' },
  { v: 'renewals', n: 'Renewals', i: 'refresh', c: 'g-blue', path: '/renewals' },
  { v: 'demo-bookings', n: 'Demo bookings', i: 'cal', c: 'g-cyan', path: '/demo-bookings' },
  { v: 'demo-reports', n: 'Demo reports', i: 'chart', c: 'g-purple', path: '/demo-reports' },
  { g: 'MARKETING' },
  { v: 'marketing', n: 'Campaigns', i: 'mega', c: 'g-pink', path: '/marketing' },
  { v: 'mkt-email', n: 'Email', i: 'mail', c: 'g-blue', path: '/marketing/email' },
  { v: 'mkt-whatsapp', n: 'WhatsApp', i: 'chat', c: 'g-green', path: '/marketing/whatsapp' },
  /* Hidden, not removed: the pages, routes and data stay, and taking the
     flag off puts the menu back. Nobody is sending SMS or running journeys
     yet, and a menu to something unused is a menu people click by mistake. */
  { v: 'mkt-sms', n: 'SMS', i: 'sms', c: 'g-amber', path: '/marketing/sms', hidden: true },
  { v: 'mkt-push', n: 'Push', i: 'bell', c: 'g-purple', path: '/marketing/push' },
  { v: 'mkt-journeys', n: 'Journeys', i: 'flow', c: 'g-red', path: '/marketing/journeys', hidden: true },
  { v: 'mkt-studio', n: 'AI Studio', i: 'wand', c: 'g-pink', path: '/marketing/studio' },
  { v: 'mkt-contacts', n: 'Contacts', i: 'users', c: 'g-pink', path: '/marketing/contacts' },
  /* Off the rail, like Journeys: a campaign picks its audience in the box
     where it is written, so the list had no reason to be a destination.
     The page is still there and still reachable by its address. */
  { v: 'mkt-audiences', n: 'Audiences', i: 'target', c: 'g-cyan', path: '/marketing/audiences', hidden: true },
  { v: 'mkt-templates', n: 'Templates', i: 'file', c: 'g-orange', path: '/marketing/templates' },
  { g: 'URL TRACKING' },
  { v: 'utm-dashboard', n: 'Dashboard', i: 'grid', c: 'g-purple', path: '/url-tracking' },
  { v: 'utm-campaigns', n: 'Campaigns', i: 'mega', c: 'g-pink', path: '/url-tracking/campaigns' },
  { v: 'utm-links', n: 'Tracking URLs', i: 'link', c: 'g-cyan', path: '/url-tracking/links' },
  /*
   * Creating is a verb on the Tracking URLs menu rather than a menu of its
   * own — there is no separate thing to be allowed to see. `perm` says which
   * menu and verb gate the entry, so somebody who may read the list but not
   * add to it is not offered a door that will not open.
   */
  {
    v: 'utm-create', n: 'Create URL', i: 'plus', c: 'g-green',
    path: '/url-tracking/create', perm: ['utm-links', 'create']
  },
  { v: 'utm-analytics', n: 'Analytics', i: 'chart', c: 'g-blue', path: '/url-tracking/analytics' },
  { g: 'WORKSPACE' },
  { v: 'queue', n: 'Needs Action', i: 'alert', c: 'g-red', path: '/needs-action' },
  { v: 'billing', n: 'Billing', i: 'refresh', c: 'g-blue', path: '/billing' },
  { v: 'outbox', n: 'Outbox', i: 'card', c: 'g-orange', path: '/outbox' },
  { v: 'copilot', n: 'AI Copilot', i: 'zap', c: 'g-pink', path: '/copilot' },
  { v: 'insights', n: 'Insights', i: 'chart', c: 'g-cyan', path: '/insights' },
  { v: 'settings', n: 'Settings', i: 'gear', c: 'g-slate', path: '/settings' }
];

/** Title, subtitle, icon and accent for each page header. */
export const PAGE_META = {
  dashboard: ['Dashboard', 'The client book counted by plan, promo code, payment and who is actually using it', 'grid', 'g-purple'],
  clients: ['Clients', 'Manage accounts, plans and renewals', 'users', 'g-green'],
  trials: ['Trials', 'Accounts inside their trial window', 'clock', 'g-amber'],
  payments: ['Payments', 'Money that has not landed yet', 'card', 'g-orange'],
  renewals: ['Renewals', 'Expiring soon, or already expired', 'refresh', 'g-blue'],
  'follow-ups': ['Follow-ups', 'What every demo left for somebody to do', 'flag', 'g-amber'],
  'demo-bookings': ['Demo bookings', 'Everything booked on Calendly: who is coming, who came, who did not', 'cal', 'g-cyan'],
  'demo-reports': ['Demo reports', 'The demo book counted by day, week, month or year', 'chart', 'g-purple'],
  'sales-reports': ['Sales reports', 'What came in, and what became of it — by source, and by whose name is on it', 'chart', 'g-purple'],
  queue: ['Needs Action', 'Everything flagged, worst first', 'alert', 'g-red'],
  sales: ['Sales overview', 'Pipeline, forecast and what to push today', 'grid', 'g-purple'],
  pipeline: ['Pipeline', 'Demo requests, worked stage by stage', 'zap', 'g-green'],
  deals: ['Deals', 'Every opportunity, open and closed', 'card', 'g-orange'],
  contacts: ['Contacts', 'People and leads', 'users', 'g-blue'],
  companies: ['Companies', 'Accounts you sell to', 'building', 'g-cyan'],
  activities: ['Activities', 'Calls, emails and follow-ups', 'cal', 'g-pink'],
  marketing: ['Campaigns', 'Reach a segment across any channel', 'mega', 'g-pink'],
  'mkt-email': ['Email', 'Campaigns, templates and reach on email', 'mail', 'g-blue'],
  'mkt-whatsapp': ['WhatsApp', 'Campaigns, templates and reach on WhatsApp', 'chat', 'g-green'],
  'mkt-sms': ['SMS', 'Campaigns, templates and reach on SMS', 'sms', 'g-amber'],
  'mkt-push': ['Push', 'Campaigns, templates and reach on push', 'bell', 'g-purple'],
  'mkt-journeys': ['Journeys', 'Sequences that run over days on their own', 'flow', 'g-red'],
  'mkt-studio': ['AI Studio', 'Build sequences, segments and copy', 'wand', 'g-pink'],
  'mkt-contacts': ['Contacts', "Marketing's own list, separate from Sales", 'users', 'g-pink'],
  'mkt-audiences': ['Audiences', 'Live segments of the book', 'target', 'g-cyan'],
  'mkt-templates': ['Templates', 'Write once, reuse anywhere', 'file', 'g-orange'],
  'utm-dashboard': ['URL Tracking', 'Every marketing link, and what it earned', 'grid', 'g-purple'],
  'utm-campaigns': ['Tracking campaigns', 'Group the links that belong together', 'mega', 'g-pink'],
  'utm-links': ['Tracking URLs', 'Short links, their UTM tags and their clicks', 'link', 'g-cyan'],
  'utm-create': ['Create tracking URL', 'Build a short link with its UTM tags', 'plus', 'g-green'],
  'utm-analytics': ['Click analytics', 'Which source, message and device is working', 'chart', 'g-blue'],
  billing: ['Billing', 'Recurring schedule and collections', 'refresh', 'g-blue'],
  outbox: ['Outbox', 'Drafts waiting to be sent', 'card', 'g-orange'],
  copilot: ['AI Copilot', 'Ask anything about your clients', 'zap', 'g-pink'],
  insights: ['Insights', 'Conversion, ageing and promo performance', 'chart', 'g-cyan'],
  settings: ['Settings', 'Account, workspace, display and data', 'gear', 'g-slate']
};

/**
 * Views that carry a built-in flag filter.
 *
 * Each of the three is one question, and two of them were answering a
 * different one:
 *
 *   Trials    who is inside their trial window right now. A trial runs
 *             `S.trialDays` (14), so this is "who is in the fourteen days".
 *   Payments  who has paid. It was `unpaid` — the chase list — which is a
 *             fair screen to want but it is not what the menu says, and the
 *             number beside Payments counted the opposite of the page.
 *   Renewals  who has just lapsed: expiry passed, inside the last fourteen
 *             days. It was `renewing`, which mixed those still running with
 *             those gone two years ago, so the one list you can act on was
 *             buried in a thousand you cannot.
 */
export const PRESET_FLAGS = { trials: 'in-trial', payments: 'paid', renewals: 'expired-14' };

/** How long "just lapsed" is, in days. */
export const LAPSED_DAYS = 14;

/* The two promo answers that are not a code. Values rather than labels, so
   they cannot collide with a real code — a code is eight characters of
   A-Z0-9 and neither of these is. */
export const PROMO_ANY = '__any';
export const PROMO_NONE = '__none';

export const ROLES = ['Owner', 'Admin', 'Manager', 'Agent', 'Viewer'];

/*
 * Mirrors PERMISSIONS in backend/src/auth.js.
 *
 * The server is the authority and the team screen reads the live matrix from
 * /auth/me; this is what it falls back to offline, where there is nothing to
 * ask. Keep the two in step — they are a description of the same rules.
 */
export const ROLE_PERMISSIONS = {
  Owner:   ['view', 'edit', 'delete', 'export', 'settings', 'ai', 'billing', 'users'],
  Admin:   ['view', 'edit', 'delete', 'export', 'settings', 'ai', 'billing', 'users'],
  Manager: ['view', 'edit', 'delete', 'export', 'ai', 'billing'],
  Agent:   ['view', 'edit', 'ai'],
  Viewer:  ['view']
};

export const PERM_LABELS = {
  view: 'View records',
  edit: 'Create and edit',
  delete: 'Delete',
  export: 'Export and backup',
  settings: 'Change settings',
  ai: 'Use AI',
  billing: 'Billing',
  users: 'Manage team'
};

export const TIMEZONES = [
  'Asia/Dubai', 'Asia/Riyadh', 'Asia/Karachi', 'Asia/Kolkata',
  'Europe/London', 'Europe/Berlin', 'America/New_York', 'UTC'
];

export const BRANDS = [
  ['#6366F1', '#8B5CF6', 'Indigo'],
  ['#0E9F6E', '#10B981', 'Emerald'],
  ['#0EA5E9', '#3B82F6', 'Sky'],
  ['#D946EF', '#EC4899', 'Fuchsia'],
  ['#F97316', '#F59E0B', 'Amber'],
  ['#0F766E', '#14B8A6', 'Teal']
];

/** What an automation rule can fire on. */
export const TRIGGERS = [
  ['upsell', 'Add-on upsell opportunity'],
  ['due-soon', 'Payment due soon'],
  ['overdue', 'Payment overdue'],
  ['trial-soon', 'Trial ending soon'],
  ['trial-over', 'Trial ended unpaid'],
  ['unpaid', 'Payment not received'],
  ['expired', 'Subscription expired'],
  ['renewing', 'Renewal approaching'],
  ['stale', 'Gone quiet'],
  ['unowned', 'No owner assigned'],
  ['nocontact', 'No contact person']
];

export const triggerName = t => (TRIGGERS.find(x => x[0] === t) || [t, t])[1];

/** Everything the alert engine can raise, for Settings -> Alert rules. */
export const ALERT_TYPES = [
  ['deal-unowned', 'Deal unowned'],
  ['close-passed', 'Deal close date passed'],
  ['deal-stalled', 'Deal gone quiet'],
  ['deal-nonext', 'Deal has no next step'],
  ['deal-unconverted', 'Won deal not converted'],
  ['act-overdue', 'Activity overdue'],
  ['act-due', 'Activity due soon'],
  ['upsell', 'Add-on upsell opportunity'],
  ['overdue', 'Payment overdue'],
  ['due-soon', 'Payment due soon'],
  ['nocycle', 'Paid plan with no cycle'],
  ['underpriced', 'Charge does not cover its cycle'],
  ['unowned', 'No owner assigned'],
  ['trial-over', 'Trial ended unpaid'],
  ['trial-soon', 'Trial ending soon'],
  ['expired', 'Subscription expired'],
  ['renewing', 'Renewal approaching'],
  ['unpaid', 'Payment not received'],
  ['stale', 'Gone quiet'],
  ['nocontact', 'No contact person'],
  ['notrialdate', 'Trial with no end date']
];

/** Per-event notification routing, for Settings -> Notifications. */
/**
 * Which menu gates a view - the entry's own key, or the menu its `perm`
 * names for entries that are a verb on another menu (Create URL).
 */
export const menuOfView = view => {
  const entry = NAV.find(n => n.v === view);
  if (!entry) return view;
  return Array.isArray(entry.perm) ? entry.perm[0] : (entry.perm || entry.v);
};

/**
 * Where a login lands: the configured start page if the role can open it,
 * otherwise the first page on the rail it can. A role with only URL tracking
 * used to land on the client dashboard - a screen its rail did not offer and
 * its permissions did not cover.
 */
export function homeFor(menus, S = {}) {
  const may = view => !menus || ((menus[menuOfView(view)] || []).includes('view'));
  const wanted = S.startView === 'queue' ? 'needs-action' : (S.startView || 'dashboard');
  const wantedEntry = NAV.find(n => n.path && (n.v === wanted || n.path === `/${wanted}`));
  if (wantedEntry ? may(wantedEntry.v) : may(wanted)) return wantedEntry ? wantedEntry.path : `/${wanted}`;
  const first = NAV.find(n => n.path && !n.hidden && may(n.v));
  return first ? first.path : '/settings/profile';
}

export const NOTIFY_TAGS = [
  ['overdue', 'Payment overdue'],
  ['due-soon', 'Payment due soon'],
  ['trial-over', 'Trial ended unpaid'],
  ['trial-soon', 'Trial ending soon'],
  ['expired', 'Subscription expired'],
  ['renewing', 'Renewal approaching'],
  ['unpaid', 'Payment not received'],
  ['stale', 'Client gone quiet'],
  ['unowned', 'No owner assigned'],
  ['deal-stalled', 'Deal gone quiet'],
  ['close-passed', 'Deal close date passed'],
  ['deal-unconverted', 'Won deal not converted'],
  ['act-overdue', 'Activity overdue'],
  ['act-due', 'Activity due soon'],
  ['upsell', 'Upsell opportunity'],
  ['demo-new', 'New demo booked'],
  ['demo-today', 'Demo today'],
  ['demo-outcome', 'Demo ended, outcome not recorded'],
  ['demo-noshow', 'Demo no-show to follow up'],
  ['demo-cancel', 'Demo cancelled by the invitee'],
  ['demo-register', 'Demo done, still not registered']
];

export const NOTIFY_CHANNELS = [
  ['app', 'In-app'],
  ['desktop', 'Desktop'],
  /* Email to the record's owner - or to every admin when it has none. Off
     until switched on per event: the server used to send it for every
     severity-2 alert with no way to stop it. */
  ['email', 'Email'],
  ['digest', 'Daily digest'],
  ['slack', 'Slack']
];

/** Settings sub-navigation: group -> [key, label, icon]. */
export const SETTINGS_NAV = [
  ['ACCOUNT', [
    ['profile', 'Profile', 'users'],
    ['org', 'Organisation', 'building'],
    ['team', 'Team & roles', 'users'],
    ['depts', 'Departments', 'building'],
    ['notif', 'Notifications', 'bell'],
    ['sec', 'Security', 'flag']
  ]],
  ['WORKSPACE', [
    ['lists', 'Lists', 'list'],
    ['sales', 'Sales', 'zap'],
    ['rules', 'Rules', 'flag'],
    ['alerts', 'Alert rules', 'alert'],
    ['auto', 'Automation', 'refresh'],
    ['ai', 'AI', 'zap']
  ]],
  ['DISPLAY', [
    ['columns', 'Columns', 'grid'],
    ['appearance', 'Appearance', 'sun']
  ]],
  ['DATA', [
    ['data', 'Import & export', 'down'],
    ['adv', 'Advanced', 'gear']
  ]]
];

/** Suggested questions on an empty Copilot screen. */
export const COPILOT_PROMPTS = [
  'Who should I chase first today?',
  'Why is trial conversion low?',
  'Which owner has the most revenue at risk?',
  'Which promo codes are wasting money?',
  'Draft a plan to recover the expired accounts',
  'Where is my MRR concentrated?'
];
